import React, { createContext, useContext, useEffect, useRef, useState } from 'react'
import { supabase } from '../lib/supabase'

const AuthContext = createContext(null)

// ─────────────────────────────────────────────────────────────────────────────
// AuthProvider
// ─────────────────────────────────────────────────────────────────────────────
// Single source of truth for the auth state. The pattern here is the canonical
// supabase-js v2 React pattern:
//
//   1. Subscribe to onAuthStateChange FIRST (synchronously, in useEffect).
//      This guarantees we never miss an event between mount and subscription.
//   2. Immediately call getSession() to hydrate the current state for any
//      session already in localStorage.
//   3. ALL auth state changes flow through the same code path — there is no
//      separate handling for "first load" vs "later events". onAuthStateChange
//      fires synthetically for cached sessions on mount (INITIAL_SESSION), so
//      both paths converge on the same handler.
//   4. Profile fetches are deduplicated by user ID using a ref, so we never
//      run two parallel fetchProfile() calls for the same user.
//
// What this fixes vs the previous version:
//   - No more race between getSession() and onAuthStateChange both calling
//     fetchProfile() in parallel.
//   - No more broken "initialEventSeen" dedup logic that misfired when
//     SIGNED_IN arrived before INITIAL_SESSION.
//   - No more "have to refresh" bug — even a slow first profile fetch leaves
//     the app in a consistent state because there's only one path to set it.
//   - No more infinite spinner — loading is set to false in a `finally` so
//     it can never get stuck.
// ─────────────────────────────────────────────────────────────────────────────

export function AuthProvider({ children }) {
  const [user, setUser]       = useState(null)
  const [profile, setProfile] = useState(null)
  const [loading, setLoading] = useState(true)

  // Tracks the user ID we're currently fetching a profile for, so concurrent
  // auth events don't kick off duplicate requests. The ref is checked and set
  // synchronously inside handleSession().
  const fetchingProfileFor = useRef(null)

  useEffect(() => {
    let cancelled = false

    // ─── Unified handler for all session changes ───────────────────────────
    // Called from both onAuthStateChange and the initial getSession().
    async function handleSession(session, eventName) {
      if (cancelled) return

      const nextUser = session?.user ?? null

      // Recovery flow has its own listener on the ResetPassword page.
      // Don't interfere with it.
      if (eventName === 'PASSWORD_RECOVERY') return
      if (eventName === 'SIGNED_IN' && window.location.hash.includes('type=recovery')) return

      // Signed out — clear everything and we're done.
      if (!nextUser) {
        setUser(null)
        setProfile(null)
        fetchingProfileFor.current = null
        setLoading(false)
        return
      }

      // Update the user immediately so anything depending on `isAuthenticated`
      // can render — ProtectedRoute uses (loading || !profile) as its gate.
      setUser(nextUser)

      // TOKEN_REFRESHED fires roughly every hour. The user is the same, the
      // profile is the same — skip the refetch.
      if (eventName === 'TOKEN_REFRESHED' && profile?.id === nextUser.id) {
        return
      }

      // Dedup: if we're already fetching this user's profile, don't start
      // another fetch. This is the single biggest fix vs the previous
      // implementation, which could fire two fetches in parallel.
      if (fetchingProfileFor.current === nextUser.id) return
      fetchingProfileFor.current = nextUser.id

      try {
        await fetchProfile(nextUser.id)
      } finally {
        // Clear the dedup marker only if it still points at this user. If a
        // sign-out happened mid-fetch, the marker is already null — don't
        // re-null it (race-safe).
        if (fetchingProfileFor.current === nextUser.id) {
          fetchingProfileFor.current = null
        }
        if (!cancelled) setLoading(false)
      }
    }

    // ─── 1. Subscribe first ───────────────────────────────────────────────
    // Listening before we hydrate prevents missed events in the gap between
    // mount and subscription.
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => { handleSession(session, event) }
    )

    // ─── 2. Then hydrate the existing session, if any ─────────────────────
    // onAuthStateChange will ALSO fire INITIAL_SESSION shortly after, but the
    // dedup ref above ensures both paths share a single profile fetch.
    supabase.auth.getSession().then(({ data: { session } }) => {
      handleSession(session, 'INITIAL_HYDRATION')
    }).catch((err) => {
      // If getSession itself fails (e.g. corrupted localStorage), we still
      // need to leave loading=false so the UI can show /login.
      console.error('getSession failed:', err)
      if (!cancelled) {
        setUser(null)
        setProfile(null)
        setLoading(false)
      }
    })

    return () => {
      cancelled = true
      subscription.unsubscribe()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // ─── Profile fetch with one-shot create-on-missing recovery ─────────────
  async function fetchProfile(userId) {
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .single()

    if (error && error.code === 'PGRST116') {
      // Row doesn't exist — DB trigger didn't fire. Create it manually from
      // the auth user metadata, then re-read. Uses upsert so a race with a
      // late-firing trigger doesn't blow up on the unique constraint.
      const { data: userData } = await supabase.auth.getUser()
      const meta = userData?.user?.user_metadata || {}

      const { error: upsertErr } = await supabase.from('profiles').upsert({
        id:              userId,
        full_name:       meta.full_name       || '',
        email:           userData?.user?.email || '',
        country:         meta.country         || null,
        native_language: meta.native_language || null,
        situation:       meta.situation       || null,
        why_here:        meta.why_here        || null,
      }, { onConflict: 'id' })

      if (upsertErr) {
        console.error('Profile upsert failed:', upsertErr)
        return
      }

      const { data: newData } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single()

      if (newData) setProfile(newData)
      return
    }

    if (error) {
      console.error('Profile fetch failed:', error)
      return
    }

    setProfile(data)
  }

  // ─── Auth actions ────────────────────────────────────────────────────────

  async function signUp({ email, password, fullName, metadata = {} }) {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name:         fullName,
          country:           metadata.country           || null,
          native_language:   metadata.native_language   || null,
          situation:         metadata.situation         || null,
          why_here:          metadata.why_here          || null,
          terms_accepted_at: metadata.terms_accepted_at || null,
          terms_version:     metadata.terms_version     || '2026-05',
        },
      },
    })

    // Brevo subscription is non-fatal — never block signup on it.
    if (!error) {
      try {
        await fetch('/.netlify/functions/auth', {
          method:  'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            action:   'brevo_subscribe',
            email:    email.trim().toLowerCase(),
            fullName: fullName,
          }),
        })
      } catch (e) {
        console.warn('Brevo subscribe failed silently:', e)
      }
    }

    return { data, error }
  }

  async function signIn({ email, password }) {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    })
    // onAuthStateChange will fire SIGNED_IN and the handler above will
    // populate user + profile. Callers should NOT redirect from here —
    // wait on isAuthenticated to flip in the parent component instead.
    return { data, error }
  }

  async function signOut() {
    // Clear local state immediately for snappy UX. The user sees the sign-out
    // happen instantly rather than waiting for the network round-trip.
    setUser(null)
    setProfile(null)
    fetchingProfileFor.current = null
    setLoading(false)

    // Fire the real sign-out in the background. The onAuthStateChange listener
    // will fire SIGNED_OUT, our handler will see a null session and short-
    // circuit (it's already cleared), so there's no double-clear.
    supabase.auth.signOut().catch(err => {
      console.warn('Background signOut failed (local state already cleared):', err)
    })
  }

  async function refreshProfile() {
    if (user) await fetchProfile(user.id)
  }

  const value = {
    user,
    profile,
    loading,
    signUp,
    signIn,
    signOut,
    refreshProfile,
    isAuthenticated:    !!user,
    tier:               profile?.subscription_tier  ?? 0,
    selectedTree:       profile?.selected_tree      ?? null,
    onboardingComplete: profile?.onboarding_complete ?? false,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) throw new Error('useAuth must be used within AuthProvider')
  return context
}
